import { useEffect, useState } from 'react';

type DateInput = string | number | Date | null | undefined;

function safeTz(tz?: string | null): string | undefined {
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

export function toDate(v: DateInput): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "3:42 PM" (or "3:42:18 PM") in the tenant timezone, falling back to the browser timezone. */
export function fmtTime(v: DateInput, tz?: string | null, withSec = false): string {
  const d = toDate(v);
  if (!d) return '—';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(withSec ? { second: '2-digit' as const } : {}),
    hour12: true,
    timeZone: safeTz(tz),
  }).format(d);
}

/** "Thu, Sep 17, 2026" */
export function fmtDate(v: DateInput, tz?: string | null): string {
  const d = toDate(v);
  if (!d) return '—';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: safeTz(tz),
  }).format(d);
}

/** "Sep 17, 3:42 PM" — the date part is omitted when it is today in that timezone. */
export function fmtDateTime(v: DateInput, tz?: string | null, withSec = false): string {
  const d = toDate(v);
  if (!d) return '—';
  const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: safeTz(tz) });
  const t = fmtTime(d, tz, withSec);
  return day.format(new Date()) === day.format(d) ? t : `${day.format(d)}, ${t}`;
}

/** Short timezone abbreviation, e.g. "CDT". */
export function tzAbbr(tz?: string | null, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: safeTz(tz), timeZoneName: 'short' }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** Current epoch ms, re-rendering the caller every `intervalMs` (default 1 s). */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** 42 → "42 s", 272 → "4:32", 4000 → "1 h 6 min" */
export function fmtSeconds(totalSec: number): string {
  const s = Math.max(0, Math.round(totalSec));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m >= 60) return `${Math.floor(m / 60)} h ${m % 60} min`;
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** "in 42 s" / "3:10 ago" */
export function fmtRelative(target: DateInput, now: number): string {
  const d = toDate(target);
  if (!d) return '—';
  const diff = (d.getTime() - now) / 1000;
  return diff >= 0 ? `in ${fmtSeconds(diff)}` : `${fmtSeconds(-diff)} ago`;
}

/** "12 s ago", "5 min ago", "2 h ago" */
export function fmtAgo(target: DateInput, now: number): string {
  const d = toDate(target);
  if (!d) return '—';
  const s = Math.max(0, (now - d.getTime()) / 1000);
  if (s < 90) return `${Math.round(s)} s ago`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min ago`;
  return `${Math.round(m / 60)} h ago`;
}

/** 30000 → "30 s", 300000 → "5 min" */
export function fmtInterval(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = s / 60;
  return `${Number.isInteger(m) ? m : m.toFixed(1)} min`;
}

export function msSince(v: DateInput, now: number): number | null {
  const d = toDate(v);
  return d ? now - d.getTime() : null;
}
